// Quick-profile chip strip rendered inside HelperJourneyHero. One question
// at a time, chip answers, auto-advance on tap. Captures donor-specific
// signal (education, looks, prior donations, openness) that medical
// history doesn't cover.
//
// v1 stores answers in local state only — backend schema for the donor-
// profile fields and bidirectional MH sync are tracked under
// EXECUTION_PLAN.md task 1.5 part 6. Carrier quick-profile is deferred
// because most carrier-relevant signal lives in MH and we want the sync
// pattern in place before asking redundantly.
import { useState } from "react";
import { View, Text, Pressable } from "react-native";
import { EMBER } from "@louise/moon-ui/theme/tokens";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
type Question = {
  id: string;
  prompt: string;
  options: string[];
};

const DONOR_QUESTIONS: Question[] = [
  {
    id: "education",
    prompt: "Highest level of education?",
    options: ["High school", "Some college", "College", "Postgrad"],
  },
  {
    id: "eye_color",
    prompt: "Eye color?",
    options: ["Brown", "Blue", "Green", "Hazel", "Other"],
  },
  {
    id: "hair_color",
    prompt: "Hair color?",
    options: ["Black", "Brown", "Blonde", "Red", "Gray"],
  },
  {
    id: "prior_donations",
    prompt: "Have you donated before?",
    options: ["Never", "Once", "More than once"],
  },
  {
    id: "openness",
    prompt: "Open to being known to recipients?",
    options: ["Yes", "Maybe", "Anonymous-only"],
  },
];

type Props = {
  role: "donor" | "carrier";
};

export default function HelperQuickProfile({ role }: Props) {
  const capturePress = usePressCapture("HelperQuickProfile");
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const questions = role === "donor" ? DONOR_QUESTIONS : [];

  // Carrier deferred — see EXECUTION_PLAN task 1.5 part 6.
  if (questions.length === 0) return null;

  const remaining = questions.filter((q) => !answers[q.id]);
  const isComplete = remaining.length === 0;
  const current = remaining[0];
  const answeredCount = questions.length - remaining.length;

  const onAnswer = (id: string, value: string) => {
    setAnswers((prev) => ({ ...prev, [id]: value }));
  };

  return (
    <View
      style={{
        marginTop: 4,
        paddingTop: 16,
        borderTopWidth: 1,
        borderTopColor: `${EMBER.colors.textLight}20`,
        gap: 10,
      }}
    >
      <View
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            fontWeight: "600",
            color: EMBER.colors.text,
          }}
        >
          {isComplete ? "Quick profile complete" : "Quick profile"}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 11,
            fontWeight: "600",
            color: EMBER.colors.textLight,
          }}
        >
          {isComplete
            ? `${questions.length} of ${questions.length}`
            : `Question ${answeredCount + 1} of ${questions.length}`}
        </Text>
      </View>

      {isComplete ? (
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 12,
            color: EMBER.colors.textLight,
            lineHeight: 17,
          }}
        >
          Thanks — we&apos;ll match these to recipient preferences when your
          profile goes live.
        </Text>
      ) : current ? (
        <>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 13,
              color: EMBER.colors.textLight,
              lineHeight: 18,
            }}
          >
            {current.prompt}
          </Text>
          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              gap: 6,
            }}
          >
            {current.options.map((opt) => (
              <Pressable
                key={opt}
                onPress={() => {
                  capturePress(pressLabel(opt), "helper_profile_answer");
                  onAnswer(current.id, opt);
                }}
                style={({ pressed }) => ({
                  paddingHorizontal: 12,
                  paddingVertical: 7,
                  borderRadius: 999,
                  borderWidth: 1,
                  borderColor: "rgba(184, 74, 22, 0.15)",
                  backgroundColor: pressed
                    ? "rgba(184, 74, 22, 0.16)"
                    : "rgba(184, 74, 22, 0.08)",
                })}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 12,
                    fontWeight: "600",
                    color: EMBER.colors.warm,
                  }}
                >
                  {opt}
                </Text>
              </Pressable>
            ))}
          </View>
        </>
      ) : null}
    </View>
  );
}
